import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import { getSubscriptionPlans } from "../../../lib/planRequests";
import { bottomText, planOptions, tradePlans } from "../../../utils/user";
import { PlanCards } from "./PlanCards";
import TextField from "../../global/Inputs/TextField";
import Button from "../../global/Buttons/Button";
import { AxiosError } from "axios";
import { useToast } from "../../../context/toastContext";
import { applyCoupon } from "../../../context/authContext/actions";

export const Plan = () => {
  const infoEmail = "info@vinous.com";
  const router = useRouter();
  const [activeTab, setActiveTab] = useState(true);
  const [consumerPlans, setConsumersPlan] = useState([]);
  const [wineIndustryPlans, setWineIndustryPlans] = useState(tradePlans);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [optionSelected, setOptionSelected] = useState<string | planOptions>(
    ""
  );
  const [haveDiscountCode, setHaveDiscountCode] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [isApplingCoupon, setIsApplingCoupon] = useState(false);
  const [discountPlans, setDiscountPlan] = useState<
    Array<{ planId: number; discount: number; discountCode: string }>
  >([]);
  const toast = useToast(2500);

  const toggleTab = (index: boolean) => {
    setActiveTab(index);
    router.replace({
      query: {
        ...router.query,
        selected: index ? "consumer" : "trade",
      },
    });
  };

  const handleApplyCoupon = useCallback(async () => {
    try {
      setIsApplingCoupon(true);
      const { data } = await applyCoupon(discountCode);
      const plan = consumerPlans.find((plan) => plan.id === data?.plan_id);
      const discountedPlan = plan
        ? plan
        : Object.values(wineIndustryPlans).find(
            (plan) => plan.id === data?.plan_id
          );
      if (discountedPlan) {
        const codeAlreadyApplied = discountPlans.some(
          (plan) => plan.discountCode === discountCode.toLowerCase()
        );
        if (!codeAlreadyApplied)
          updateDiscountPlans(data, discountedPlan, discountCode);
      } else {
        toast("error", `Coupon could not be applied to any plan`);
      }
    } catch (err: AxiosError | any) {
      toast("error", `Oops, ${err?.data?.error}`);
    } finally {
      setIsApplingCoupon(false);
    }
  }, [applyCoupon, discountCode, toast, discountPlans]);

  const updateDiscountPlans = useCallback(
    (couponInfo: any, discountedPlan: any, discountCode: string) => {
      const newDiscountPlans = JSON.parse(JSON.stringify(discountPlans));
      const planWithDiscount = {
        planId: discountedPlan.id,
        discount: couponInfo?.amount_off_in_cents / 100,
        discountCode: discountCode.toLowerCase(),
      };
      const index = newDiscountPlans.findIndex(
        (plan) => plan.planId === discountedPlan.id
      );

      if (index !== -1) newDiscountPlans[index] = planWithDiscount;
      else newDiscountPlans.push(planWithDiscount);

      setDiscountPlan(newDiscountPlans);

      toast(
        "success",
        `The discount code "${discountCode}" has been applied to ${discountedPlan.name}`
      );
    },
    [discountPlans]
  );

  useEffect(() => {
    const fragment = window.location.hash.substring(1); 
    if (fragment) {
      router.replace({
        query: {
          ...router.query,
          selected: fragment,
        },
      });
    }
    
    async function getStripeProducts() {
      try {
        const { data } = await getSubscriptionPlans();
        const consumer = data.consumer_plans;
        const plans = consumer.map(plan => ({...plan, isRedirecting: false}));
        setConsumersPlan(plans);
      } catch {
      } finally {
        setLoadingPlans(false);
      }
    }
    getStripeProducts();
  }, []);

  useEffect(() => {
    if (router.isReady && router.query["selected"]) {
      setOptionSelected(router.query["selected"] as string);
      setActiveTab(router.query["selected"] === "consumer");
    }
  }, [router]);

  return (
    <div className="flex-col justify-center items-center">
      {optionSelected === "" ? (
        <div className="pb-4 text-center flex-col">
          <h1 className="text-2xl	font-bold	">
            Let&apos;s find a plan that&apos;s right for you.
          </h1>
          Please select the option that describes you the best.
          <div>
            <button
              className={"p-3 m-2 rounded w-60 text-white bg-red"}
              onClick={() => {
                setActiveTab(true);
                router.replace({
                  query: {
                    ...router.query,
                    selected: "consumer",
                  },
                });
              }}
            >
              I am a consumer
            </button>
            <button
              className={"p-3 rounded w-60 m-4 text-white bg-red"}
              onClick={() => {
                setActiveTab(false)
                router.replace({
                  query: {
                    ...router.query,
                    selected: "trade",
                  },
                });
              }

              }
            >
              I work in the Wine Industry
            </button>
          </div>
        </div>
      ) : (
        <>
          <PlanCards
            activeTab={activeTab}
            toggleTab={toggleTab}
            loadingPlans={loadingPlans}
            consumerPlans={consumerPlans}
            wineIndustryPlans={wineIndustryPlans}
            discountPlans={discountPlans}
            setConsumerPlans={setConsumersPlan}
            setWineIndustryPlans={setWineIndustryPlans}
          />
          <div className="flex justify-between m-4 xs:flex-col md:flex-row">
            {haveDiscountCode ? (
              <div className="flex space-x-2">
                <TextField
                  required
                  label=""
                  name="discountCode"
                  value={discountCode}
                  onChange={(event: React.FormEvent<HTMLInputElement>) =>
                    setDiscountCode(event.currentTarget.value)
                  }
                  placeholder="Discount Code"
                  className="w-60"
                  onEnter={handleApplyCoupon}
                />
                <Button
                  className="w-32 h-12 bg-opacity-10"
                  labelColor="text-black"
                  type={"Update"}
                  onClick={handleApplyCoupon}
                  label={"Apply"}
                  disabled={isApplingCoupon}
                  isLoading={isApplingCoupon}
                />
              </div>
            ) : (
              <button
                className="cursor-pointer hover:text-red"
                onClick={() => setHaveDiscountCode(true)}             
              >
                Have a discount code?
              </button>
            )}
            <p className="xs:mt-10 md:mt-0">
              If you require assistance, please contact us at
              <br />
              <a href={`mailto:${infoEmail}`}>{infoEmail}</a>
            </p>
          </div>
          <div>
            <p className="ml-4 mt-10 text-xs">{bottomText}</p>
          </div>
        </>
      )}
    </div>
  );
};
